import { NgClass, NgIf } from '@angular/common';
import { MatIconModule } from '@angular/material/icon';
import { ChangeDetectionStrategy, Component, Input } from '@angular/core';

import { IconsEnum } from '@enums/icons.enum';

import { DsIconSize } from '@design-system/ds-icon/types/ds-icon-size.type';
import { DsIconTheme } from '@design-system/ds-icon/types/ds-icon-theme.type';
import { DsIconFontSet } from '@design-system/ds-icon/types/ds-icon-font-set.type';
import { DS_ICON_DEFAULT_FONT_SET } from '@design-system/ds-icon/properties/ds-icon.properties';
import { DsIsRegisteredIconPipe } from '@design-system/ds-icon/pipes/ds-is-registered-icon.pipe';

@Component({
  selector: 'ds-icon',
  standalone: true,
  imports: [NgIf, NgClass, MatIconModule, DsIsRegisteredIconPipe],
  templateUrl: './ds-icon.component.html',
  styleUrls: ['./ds-icon.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DsIconComponent {
  /**
   * Icon to render. Pass a Material Symbols ligature name or an `IconsEnum` value.
   * Registered `IconsEnum` names render as SVG; everything else renders as a font icon.
   *
   * @see https://fonts.google.com/icons
   */
  @Input() name: string | IconsEnum | null = null;

  /**
   * Controls the icon size. Sizing uses design-system icon tokens; `inherit` follows the parent font size (1em).
   *
   * @default 'md'
   */
  @Input() size: DsIconSize = 'md';

  /**
   * Controls the icon color theme. Use `inherit` to keep the parent color.
   *
   * @default 'inherit'
   */
  @Input() theme: DsIconTheme = 'inherit';

  /**
   * Optional color override. When set, it takes precedence over `theme`.
   */
  @Input() color: string | null = null;

  /**
   * Optional pixel size override (for example `'48px'`). When set, it takes precedence over `size`.
   */
  @Input() iconSize: string | null = null;

  /**
   * Icon font set used when `name` is a font icon rather than a registered SVG.
   *
   * @default 'material-symbols-rounded'
   */
  @Input() fontSet: DsIconFontSet = DS_ICON_DEFAULT_FONT_SET;

  /**
   * Accessible label. When omitted, the icon is treated as decorative.
   */
  @Input() ariaLabel: string | null = null;
}
